import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../../core/store/store';
import { useInterventionsList } from './hooks/useInterventionsList';
import type { InterventionCTA } from './hooks/useInterventionsList';
import { getApiBaseUrl } from '../../../core/config/api';
import { usePanelAccess } from '../context/PanelAccessContext';
import Navbar from '../../../shared/components/Navbar';
import BackgroundImage from '../../../shared/components/BackgroundImage';
import ParticlesBackground from '../../../shared/components/ParticlesBackground';
import {
  SCENARIO_STATUS_OPTIONS,
  type ScenarioStatusValue,
  type InterventionHistoryEntry,
  normalizeScenarioStatus,
  scenarioStatusSelectRingClass,
  isScenarioReferentClient,
  formatScenarioDateTime,
  scenarioActionLabel,
} from './hooks/interventionsManageHelpers';
import type { PanelInterventionDetail } from './InterventionsCreate';

/* ─── Helpers ─── */

const inputCls =
  'rounded-lg border border-[#223046] bg-[#0b1220] px-3 py-2 text-sm text-cockpit-text placeholder-cockpit-muted focus:outline-none focus:border-[#6366f1]/60';

function statusBadge(statut: string) {
  const n = normalizeScenarioStatus(statut);
  if (n === 'actif')
    return (
      <span className="rounded px-2 py-0.5 text-[11px] font-semibold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
        Actif
      </span>
    );
  if (n === 'en_test')
    return (
      <span className="rounded px-2 py-0.5 text-[11px] font-semibold bg-sky-500/20 text-sky-300 border border-sky-500/40">
        En test
      </span>
    );
  return (
    <span className="rounded px-2 py-0.5 text-[11px] font-semibold bg-slate-500/20 text-cockpit-text-2 border border-slate-500/40">
      Brouillon
    </span>
  );
}

/* ─── Composant principal ─── */

const InterventionsManage: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const { referentFunctions } = usePanelAccess();
  const canValidateActif = isScenarioReferentClient(referentFunctions);

  // Rôle utilisateur courant — modèle 18 rôles (1 Founder … 18 Joueurs).
  const userRoleId: number = (currentUser as any)?.roles_id ?? 99;
  // Édition/suppression de N'IMPORTE quel scénario : Founder uniquement (roles_id === 1).
  const isAdminOrCm = userRoleId === 1;

  const { interventions, loading, error, reload } = useInterventionsList();

  /* États UI */
  const [search, setSearch] = useState('');
  const [filterStatut, setFilterStatut] = useState<'all' | ScenarioStatusValue>('all');
  const [filterService, setFilterService] = useState('all');

  const [spawnMsg, setSpawnMsg] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState<number | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<number | null>(null);
  const [actionMsg, setActionMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const [historyOpenId, setHistoryOpenId] = useState<number | null>(null);
  const [historyById, setHistoryById] = useState<Record<number, InterventionHistoryEntry[]>>({});
  const [historyLoadingId, setHistoryLoadingId] = useState<number | null>(null);

  const [previewId, setPreviewId] = useState<number | null>(null);
  const [previewData, setPreviewData] = useState<PanelInterventionDetail | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const [duplicateBusy, setDuplicateBusy] = useState<number | null>(null);

  /* ─── Bâtiments pour le spawn de test ─── */
  const [playerBuildings, setPlayerBuildings] = useState<{ id: number; nom: string; type: string; actif: boolean }[]>([]);
  const [spawnBuildingId, setSpawnBuildingId] = useState<number | ''>('');

  useEffect(() => {
    const token = localStorage.getItem('token') || '';
    fetch(`${getApiBaseUrl()}/api/buildings`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        if (Array.isArray(d?.data)) {
          const eligible = (d.data as any[]).filter((b) => b.actif && b.type !== 'cta');
          setPlayerBuildings(eligible);
          if (eligible.length === 1) setSpawnBuildingId(eligible[0].id);
        }
      })
      .catch(() => {});
  }, []);

  /* ─── Stats ─── */
  const stats = useMemo(() => {
    const total = interventions.length;
    const actif = interventions.filter((i) => normalizeScenarioStatus(i.statut) === 'actif').length;
    const enTest = interventions.filter((i) => normalizeScenarioStatus(i.statut) === 'en_test').length;
    const brouillon = total - actif - enTest;
    return { total, actif, enTest, brouillon };
  }, [interventions]);

  /* ─── Services disponibles ─── */
  const services = useMemo(() => {
    const set = new Set(interventions.map((i) => i.service?.toUpperCase()).filter(Boolean));
    return Array.from(set).sort();
  }, [interventions]);

  /* ─── Filtrage ─── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return interventions.filter((i) => {
      if (filterStatut !== 'all' && normalizeScenarioStatus(i.statut) !== filterStatut) return false;
      if (filterService !== 'all' && i.service?.toUpperCase() !== filterService) return false;
      if (q) {
        const titre = (i.titres?.[0] ?? '').toLowerCase();
        const cat = (i.categorie ?? '').toLowerCase();
        const id = String(i.id);
        if (!titre.includes(q) && !cat.includes(q) && !id.includes(q)) return false;
      }
      return true;
    });
  }, [interventions, search, filterStatut, filterService]);

  /* ─── Auth helpers ─── */
  const authHeaders = () => {
    const token = localStorage.getItem('token') || '';
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  /* ─── Actions ─── */
  const changeStatut = async (intervention: InterventionCTA, newStatut: ScenarioStatusValue) => {
    if (normalizeScenarioStatus(intervention.statut) === newStatut) return;
    if (newStatut === 'actif' && !canValidateActif) {
      setActionMsg({ text: 'Seul le référent scénario peut valider un scénario en « Actif ».', ok: false });
      return;
    }
    setActionMsg(null);
    setStatusBusyId(intervention.id);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/interventions/${intervention.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ statut: newStatut }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        await reload();
      } else {
        setActionMsg({ text: data.error || 'Impossible de mettre à jour le statut.', ok: false });
      }
    } catch (e: any) {
      setActionMsg({ text: e?.message || 'Erreur réseau.', ok: false });
    } finally {
      setStatusBusyId(null);
    }
  };

  const spawnTest = async (scenarioId: number) => {
    setSpawnMsg(null);
    setSpawnBusy(scenarioId);
    try {
      const body: Record<string, unknown> = {};
      if (spawnBuildingId !== '') body.building_id = spawnBuildingId;
      const res = await fetch(
        `${getApiBaseUrl()}/api/cta/interventions/${scenarioId}/panel-test`,
        { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) }
      );
      const data = await res.json();
      if (data.success) {
        setSpawnMsg(data.message ?? `Instance de test créée. Ouvrez la carte en jeu.`);
      } else {
        setSpawnMsg(data.error || 'Échec de la génération.');
      }
    } catch (e: any) {
      setSpawnMsg(e?.message || 'Erreur réseau.');
    } finally {
      setSpawnBusy(null);
    }
  };

  const openHistory = async (id: number) => {
    setHistoryOpenId(id);
    if (historyById[id]) return;
    setHistoryLoadingId(id);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/interventions/${id}/history`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      setHistoryById((prev) => ({
        ...prev,
        [id]: Array.isArray(data.data) ? data.data : [],
      }));
    } catch {
      /* silencieux */
    } finally {
      setHistoryLoadingId(null);
    }
  };

  const openPreview = async (id: number) => {
    setPreviewId(id);
    setPreviewData(null);
    setPreviewLoading(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/interventions/${id}`, {
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success && data.data) setPreviewData(data.data as PanelInterventionDetail);
    } catch {
      /* silencieux */
    } finally {
      setPreviewLoading(false);
    }
  };

  const deleteScenario = async () => {
    if (deleteConfirmId == null) return;
    setDeleteBusy(true);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/interventions/${deleteConfirmId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setActionMsg({ text: data.message || 'Scénario supprimé.', ok: true });
        await reload();
      } else {
        setActionMsg({ text: data.error || 'Erreur lors de la suppression.', ok: false });
      }
    } catch (e: any) {
      setActionMsg({ text: e?.message || 'Erreur réseau.', ok: false });
    } finally {
      setDeleteBusy(false);
      setDeleteConfirmId(null);
    }
  };

  const duplicateScenario = async (id: number) => {
    setDuplicateBusy(id);
    setActionMsg(null);
    try {
      const res = await fetch(`${getApiBaseUrl()}/api/cta/interventions/${id}/duplicate`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setActionMsg({ text: data.message || 'Scénario dupliqué.', ok: true });
        await reload();
      } else {
        setActionMsg({ text: data.error || 'Erreur lors de la duplication.', ok: false });
      }
    } catch (e: any) {
      setActionMsg({ text: e?.message || 'Erreur réseau.', ok: false });
    } finally {
      setDuplicateBusy(null);
    }
  };

  /* ─── Rendu ─── */
  return (
    <div className={embedded ? 'overflow-y-auto' : 'min-h-screen relative overflow-hidden'}>
      {!embedded && <BackgroundImage />}
      {!embedded && <ParticlesBackground />}
      {!embedded && <Navbar />}

      <div className={embedded ? '' : 'relative z-10 pt-24 pb-16'}>
        <div className={embedded ? 'max-w-7xl mx-auto' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'}>

          {/* En-tête */}
          <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-cockpit-text">Scénarios d'intervention</h1>
              <p className="text-sm text-cockpit-text-2 mt-0.5">CTA — gestion et validation des scénarios</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate('/panel/interventions/create')}
                className="rounded-lg border border-[#6366f1]/40 bg-[#6366f1]/20 px-4 py-2 text-sm font-semibold text-[#c7d2fe] hover:bg-[#6366f1]/30 transition-colors"
              >
                + Créer un scénario
              </button>
              <button
                onClick={() => reload()}
                className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
              >
                Actualiser
              </button>
              {!embedded && (
                <button
                  onClick={() => navigate('/panel')}
                  className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-2 text-sm text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
                >
                  Retour
                </button>
              )}
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
            {[
              { label: 'Total', value: stats.total, color: 'text-cockpit-text' },
              { label: 'Actifs', value: stats.actif, color: 'text-emerald-300' },
              { label: 'En test', value: stats.enTest, color: 'text-sky-300' },
              { label: 'Brouillons', value: stats.brouillon, color: 'text-cockpit-text-2' },
            ].map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 px-4 py-3 backdrop-blur-sm"
              >
                <p className="text-[11px] uppercase tracking-wide text-cockpit-muted">{s.label}</p>
                <p className={`mt-1 text-2xl font-bold ${s.color}`}>{s.value}</p>
              </div>
            ))}
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {error}
            </div>
          )}
          {actionMsg && (
            <div
              className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                actionMsg.ok
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                  : 'border-red-500/40 bg-red-500/10 text-red-200'
              }`}
            >
              {actionMsg.text}
            </div>
          )}
          {spawnMsg && (
            <div
              className={`mb-4 rounded-lg border px-3 py-2 text-sm ${
                spawnMsg.startsWith('Instance créée')
                  ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
                  : 'border-amber-500/40 bg-amber-500/10 text-amber-200'
              }`}
            >
              {spawnMsg}
            </div>
          )}

          {/* Filtres */}
          <div className="mb-4 flex flex-wrap gap-3">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par titre, catégorie, ID…"
              className={`${inputCls} flex-1 min-w-[220px]`}
            />
            <select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value as any)}
              className={inputCls}
            >
              <option value="all">Tous les statuts</option>
              {SCENARIO_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              value={filterService}
              onChange={(e) => setFilterService(e.target.value)}
              className={inputCls}
            >
              <option value="all">Tous les services</option>
              {services.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {/* Tableau */}
          <div className="rounded-xl border border-[#223046]/90 bg-[#0e1522]/90 backdrop-blur-sm overflow-hidden">
            {loading ? (
              <div className="py-16 text-center text-sm text-cockpit-text-2">Chargement des scénarios…</div>
            ) : filtered.length === 0 ? (
              <div className="py-16 text-center text-sm text-cockpit-text-2">
                {interventions.length === 0 ? (
                  <>
                    Aucun scénario créé.{' '}
                    <button
                      onClick={() => navigate('/panel/interventions/create')}
                      className="text-[#c7d2fe] hover:underline"
                    >
                      Créer le premier
                    </button>
                  </>
                ) : (
                  'Aucun résultat pour ces filtres.'
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-[#1a2537]">
                      {['ID', 'Titre', 'Service', 'Statut', 'Créé par', 'Modifié', 'Validé', 'Actions'].map(
                        (h) => (
                          <th
                            key={h}
                            className="px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-cockpit-muted"
                          >
                            {h}
                          </th>
                        )
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((inv) => {
                      const norm = normalizeScenarioStatus(inv.statut);
                      const titre = inv.titres?.[0] ?? `Scénario #${inv.id}`;
                      const isCreator =
                        !!currentUser?.username && currentUser.username === inv.created_by_username;
                      const canEdit = isCreator || isAdminOrCm;
                      const canDelete = isAdminOrCm && norm !== 'actif';

                      return (
                        <tr
                          key={inv.id}
                          className="border-b border-[#1a2537]/60 hover:bg-[#0b1220]/40 transition-colors"
                        >
                          <td className="px-4 py-3 font-mono text-xs text-cockpit-text-2">#{inv.id}</td>
                          <td className="px-4 py-3 max-w-[220px]">
                            <p className="font-medium text-cockpit-text truncate" title={titre}>
                              {titre}
                            </p>
                            {inv.categorie && (
                              <p className="text-[11px] text-cockpit-muted truncate">{inv.categorie}</p>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="rounded px-2 py-0.5 text-[11px] font-semibold bg-slate-700/50 text-cockpit-text-2">
                              {inv.service?.toUpperCase() ?? '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1.5">
                              {statusBadge(inv.statut)}
                              <select
                                value={norm}
                                disabled={statusBusyId === inv.id}
                                onChange={(e) => changeStatut(inv, e.target.value as ScenarioStatusValue)}
                                className={`min-w-[9rem] rounded border border-[#223046] bg-[#0b1220] px-2 py-1 text-[11px] text-cockpit-text focus:outline-none disabled:opacity-50 ${scenarioStatusSelectRingClass(norm)}`}
                                aria-label={`Statut scénario #${inv.id}`}
                              >
                                {SCENARIO_STATUS_OPTIONS.map((o) => (
                                  <option
                                    key={o.value}
                                    value={o.value}
                                    disabled={o.value === 'actif' && !canValidateActif}
                                  >
                                    {o.label}
                                    {o.value === 'actif' && !canValidateActif ? ' (référent)' : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-cockpit-text-2">
                            <div>{formatScenarioDateTime(inv.created_at)}</div>
                            <div className="text-cockpit-muted">{inv.created_by_username ?? '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-cockpit-text-2">
                            <div>{formatScenarioDateTime(inv.last_modified_at ?? inv.updated_at)}</div>
                            <div className="text-cockpit-muted">{inv.last_modified_by_username ?? '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-[12px] text-cockpit-text-2">
                            <div>{formatScenarioDateTime(inv.validated_at)}</div>
                            <div className="text-cockpit-muted">{inv.validated_by_username ?? '—'}</div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1.5">
                              {/* Aperçu */}
                              <button
                                type="button"
                                onClick={() => openPreview(inv.id)}
                                className="rounded border border-slate-600/60 bg-slate-800/70 px-2 py-1 text-[11px] text-cockpit-text-2 hover:bg-slate-700 transition-colors"
                                title="Aperçu du scénario"
                              >
                                Aperçu
                              </button>

                              {/* Modifier */}
                              <button
                                type="button"
                                onClick={() => navigate(`/panel/scenarios/edit/${inv.id}`)}
                                disabled={!canEdit}
                                className={`rounded border px-2 py-1 text-[11px] font-medium transition-colors ${
                                  canEdit
                                    ? 'border-[#6366f1]/40 bg-[#6366f1]/20 text-[#c7d2fe] hover:bg-[#6366f1]/30'
                                    : 'border-[#223046] bg-[#0b1220]/80 text-cockpit-muted cursor-not-allowed'
                                }`}
                                title={canEdit ? 'Modifier' : 'Modification réservée au créateur ou admin'}
                              >
                                Modifier
                              </button>

                              {/* Dupliquer */}
                              <button
                                type="button"
                                disabled={duplicateBusy === inv.id}
                                onClick={() => duplicateScenario(inv.id)}
                                className="rounded border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[11px] font-medium text-violet-300 hover:bg-violet-500/25 disabled:opacity-50 transition-colors"
                                title="Dupliquer ce scénario"
                              >
                                {duplicateBusy === inv.id ? '…' : 'Dupliquer'}
                              </button>

                              {/* Sélecteur bâtiment + Tester */}
                              {playerBuildings.length > 1 && (
                                <select
                                  value={spawnBuildingId}
                                  onChange={(e) => setSpawnBuildingId(e.target.value === '' ? '' : Number(e.target.value))}
                                  className="rounded border border-emerald-500/30 bg-[#0b1220] px-1.5 py-1 text-[11px] text-cockpit-text-2 focus:outline-none"
                                  title="Secteur de spawn (bâtiment)"
                                >
                                  <option value="">Auto</option>
                                  {playerBuildings.map((b) => (
                                    <option key={b.id} value={b.id}>{b.nom}</option>
                                  ))}
                                </select>
                              )}
                              <button
                                type="button"
                                disabled={spawnBusy === inv.id}
                                onClick={() => spawnTest(inv.id)}
                                className="rounded border border-emerald-500/40 bg-emerald-500/15 px-2 py-1 text-[11px] font-medium text-emerald-300 hover:bg-emerald-500/25 disabled:opacity-50 transition-colors"
                                title="Générer une instance test sur votre compte"
                              >
                                {spawnBusy === inv.id ? '…' : 'Tester'}
                              </button>

                              {/* Historique */}
                              <button
                                type="button"
                                disabled={historyLoadingId === inv.id}
                                onClick={() => openHistory(inv.id)}
                                className="rounded border border-amber-500/40 bg-amber-500/15 px-2 py-1 text-[11px] font-medium text-amber-300 hover:bg-amber-500/25 disabled:opacity-50 transition-colors"
                              >
                                {historyLoadingId === inv.id ? '…' : 'Historique'}
                              </button>

                              {/* Supprimer — rôle 1-2 uniquement, pas sur actif */}
                              {isAdminOrCm && (
                                <button
                                  type="button"
                                  disabled={!canDelete}
                                  onClick={() => setDeleteConfirmId(inv.id)}
                                  className={`rounded border px-2 py-1 text-[11px] font-medium transition-colors ${
                                    canDelete
                                      ? 'border-red-500/40 bg-red-500/15 text-red-300 hover:bg-red-500/25'
                                      : 'border-[#223046] bg-[#0b1220]/80 text-cockpit-muted cursor-not-allowed'
                                  }`}
                                  title={canDelete ? 'Supprimer définitivement' : 'Impossible de supprimer un scénario actif'}
                                >
                                  Supprimer
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <p className="mt-3 text-[11px] text-cockpit-muted">
            {filtered.length} scénario{filtered.length !== 1 ? 's' : ''} affiché{filtered.length !== 1 ? 's' : ''}
            {filtered.length !== interventions.length ? ` sur ${interventions.length} au total` : ''}
          </p>
        </div>
      </div>

      {/* ─── Modal Aperçu ─── */}
      {previewId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cockpit-bg-2/75 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setPreviewId(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-xl border border-[#223046]/90 bg-[#060a11] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#1a2537] px-5 py-4 shrink-0">
              <h2 className="text-sm font-semibold text-cockpit-text">
                Aperçu — Scénario #{previewId}
              </h2>
              <button
                type="button"
                onClick={() => setPreviewId(null)}
                className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1.5 text-xs text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
              >
                Fermer
              </button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4">
              {previewLoading && <p className="text-sm text-cockpit-text-2">Chargement…</p>}
              {!previewLoading && !previewData && (
                <p className="text-sm text-red-300">Impossible de charger les détails.</p>
              )}
              {previewData && (
                <>
                  <div>
                    <p className="text-[10px] uppercase text-cockpit-muted mb-1">Titre</p>
                    <p className="text-base font-bold text-cockpit-text">{previewData.titres?.[0] ?? '—'}</p>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Statut</p>
                      <p className="text-cockpit-text">{previewData.statut}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Type</p>
                      <p className="text-cockpit-text">
                        {previewData.is_tuto ? 'Tutoriel' : previewData.is_exceptionnelle ? 'Mégazone' : 'Standard'}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Temps max</p>
                      <p className="text-cockpit-text">{previewData.params?.temps_max ?? '—'} min</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Véhicules requis</p>
                      <p className="text-cockpit-text">{previewData.vehicules_requis?.length ?? 0} type(s)</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Victimes</p>
                      <p className="text-cockpit-text">
                        {previewData.params?.victimes_min ?? 0} – {previewData.params?.victimes_max ?? 0}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-0.5">Récompenses</p>
                      <p className="text-cockpit-text">
                        {previewData.recompense?.xp ?? 0} XP · {previewData.recompense?.credits ?? 0} crédits
                      </p>
                    </div>
                  </div>

                  {previewData.narrative_appel && (
                    <div className="rounded border border-slate-700/60 bg-slate-900/60 px-3 py-2">
                      <p className="text-[10px] uppercase text-cockpit-muted mb-1">Message d'appel CTA</p>
                      <p className="text-xs text-cockpit-text leading-relaxed">{previewData.narrative_appel}</p>
                    </div>
                  )}

                  {(previewData.narrative_revelations?.length ?? 0) > 0 && (
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-1.5">Révélations progressives</p>
                      <div className="space-y-1.5">
                        {previewData.narrative_revelations!.map((r, i) => (
                          <div key={i} className="rounded border border-amber-600/30 bg-amber-950/20 px-3 py-1.5">
                            <p className="text-[10px] text-amber-400 mb-0.5">
                              {r.phase === 'traveling'
                                ? "À l'arrivée des secours"
                                : r.phase === 'on_site'
                                ? 'Sur les lieux'
                                : 'Transport hospitalier'}
                            </p>
                            <p className="text-xs text-cockpit-text">{r.texte}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {previewData.memo && (
                    <div>
                      <p className="text-[10px] uppercase text-cockpit-muted mb-1">Mémo interne</p>
                      <p className="text-xs text-cockpit-text-2 leading-relaxed">{previewData.memo}</p>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Historique ─── */}
      {historyOpenId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cockpit-bg-2/75 p-4"
          role="dialog"
          aria-modal="true"
          onClick={() => setHistoryOpenId(null)}
        >
          <div
            className="w-full max-w-3xl max-h-[85vh] overflow-hidden rounded-xl border border-[#223046]/90 bg-[#060a11] shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-[#1a2537] px-5 py-4 shrink-0">
              <h2 className="text-sm font-semibold text-cockpit-text">
                Historique — Scénario #{historyOpenId}
              </h2>
              <button
                type="button"
                onClick={() => setHistoryOpenId(null)}
                className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-3 py-1.5 text-xs text-cockpit-text hover:bg-cockpit-bg-2/45"
              >
                Fermer
              </button>
            </div>
            <div className="overflow-y-auto p-5">
              {(historyById[historyOpenId] ?? []).length === 0 ? (
                <p className="text-sm text-cockpit-text-2">Aucun événement enregistré.</p>
              ) : (
                <div className="space-y-2">
                  {(historyById[historyOpenId] ?? []).map((entry) => (
                    <div
                      key={entry.id}
                      className="rounded border border-[#223046] bg-[#0b1220]/80 px-3 py-2 text-sm"
                    >
                      <div className="flex flex-wrap items-center gap-2 text-cockpit-text">
                        <span className="font-semibold text-cockpit-text">{scenarioActionLabel(entry.action)}</span>
                        <span className="text-cockpit-muted">·</span>
                        <span className="text-[12px]">{formatScenarioDateTime(entry.created_at)}</span>
                        <span className="text-cockpit-muted">·</span>
                        <span className="text-[12px]">
                          par <span className="text-cockpit-text">{entry.actor_username ?? 'Inconnu'}</span>
                        </span>
                      </div>
                      {(entry.previous_status || entry.new_status) && (
                        <p className="mt-1 text-[11px] text-cockpit-text-2">
                          Statut : {entry.previous_status ?? '—'} → {entry.new_status ?? '—'}
                        </p>
                      )}
                      {(entry.details?.changed_fields?.length ?? 0) > 0 && (
                        <p className="mt-0.5 text-[11px] text-cockpit-muted">
                          Champs : {entry.details!.changed_fields!.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal Confirmation suppression ─── */}
      {deleteConfirmId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-cockpit-bg-2/80 p-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="w-full max-w-md rounded-xl border border-red-500/40 bg-[#060a11] shadow-2xl p-6">
            <h2 className="text-base font-bold text-cockpit-text mb-2">Confirmer la suppression</h2>
            <p className="text-sm text-cockpit-text-2 mb-1">
              Vous êtes sur le point de supprimer définitivement le scénario{' '}
              <span className="font-semibold text-cockpit-text">#{deleteConfirmId}</span>.
            </p>
            <p className="text-xs text-red-300 mb-6">
              Cette action est irréversible. Tous les titres, véhicules requis, évolutions et l'historique seront supprimés.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                disabled={deleteBusy}
                onClick={() => setDeleteConfirmId(null)}
                className="rounded-lg border border-cockpit-line/60 bg-cockpit-bg-2/30 px-4 py-2 text-sm text-cockpit-text hover:bg-cockpit-bg-2/45 transition-colors"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={deleteBusy}
                onClick={deleteScenario}
                className="rounded-lg border border-red-500/50 bg-red-600 px-4 py-2 text-sm font-bold text-cockpit-text hover:bg-red-500 disabled:opacity-50 transition-colors"
              >
                {deleteBusy ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionsManage;
